/**
 * MQTT 신호 → 원본 세션 문서(`PlaySessionType`) 병합 규칙 — 순수 함수 모음.
 * ------------------------------------------------------------
 * `provider.tsx` 가 쓴다. 화면 코드가 읽는 문서 형태는 그대로 두고, 신호에 담긴 값만 갈아끼우거나
 * 조각을 다시 읽어 넣는다(FE-ADOPT-PLAN B2). 테스트: `sessionMerge.test.ts`(C1~C7).
 */
import { PlaySessionType } from '../types/PlaySession.type';

export type StateSignal = {
  round: number;
  turn: number;
  step: string;
  startAt: string | null;
  expireAt: string | null;
  scenarioId: number | null;
  paused: boolean;
};

export type TurnTeam = PlaySessionType['turn_info'][number]['teams'][number];
export type LegacyUser = PlaySessionType['users'][number];

/** 백엔드 DATETIME 문자열(KST) → ISO. 세션 문서(`currentStepExpiryDate`)와 같은 표기 */
export function toIso(value: string | null): string | null {
  if (!value) return null;
  if (value.includes('T')) return value;
  const d = new Date(`${value.replace(' ', 'T')}+09:00`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * `state` 신호 병합 — 스텝·턴·만료 시각과 일시정지 플래그만 바꾼다.
 * 원본은 일시정지를 라운드 시작 플래그(`game_start`/`round_2_start` === false)로 표현한다(`useAutoStep`).
 */
export function mergeState(
  prev: PlaySessionType,
  s: StateSignal,
): { next: PlaySessionType; turnOrStepChanged: boolean; turnChanged: boolean } {
  const turnChanged = prev.current_round !== s.round || prev.current_turn !== s.turn;
  const turnOrStepChanged = turnChanged || prev.current_step !== s.step;
  const next: PlaySessionType = {
    ...prev,
    current_round: s.round,
    current_turn: s.turn,
    current_step: s.step as PlaySessionType['current_step'],
    currentStepStartDate: toIso(s.startAt) as string,
    currentStepExpiryDate: toIso(s.expireAt) as string,
    game_start: s.round === 1 ? !s.paused : prev.game_start,
    round_2_start: s.round === 2 ? !s.paused : prev.round_2_start,
  };
  return { next, turnOrStepChanged, turnChanged };
}

/** 마지막 턴의 팀 항목 하나를 교체(없으면 추가) */
export function mergeTeam(prev: PlaySessionType, teamId: string, team: TurnTeam): PlaySessionType {
  const turnInfo = prev.turn_info.slice();
  const last = turnInfo.length - 1;
  if (last < 0) return prev;
  const teams = turnInfo[last].teams.map((t) => (t._id === teamId ? team : t));
  if (!teams.some((t) => t._id === teamId)) teams.push(team);
  turnInfo[last] = { ...turnInfo[last], teams };
  return { ...prev, turn_info: turnInfo };
}

/** 참가자 항목 하나를 교체 */
export function mergeUser(prev: PlaySessionType, userId: string, user: LegacyUser): PlaySessionType {
  return { ...prev, users: prev.users.map((u) => (u._id === userId ? user : u)) };
}

/** 같은 키의 연속 호출을 마지막 한 번으로 줄인다(FE-ADOPT-PLAN C1) */
export function createDebouncer(ms: number) {
  const timers: Record<string, ReturnType<typeof setTimeout>> = {};
  return {
    schedule(key: string, fn: () => void) {
      if (timers[key]) clearTimeout(timers[key]);
      timers[key] = setTimeout(() => {
        delete timers[key];
        fn();
      }, ms);
    },
    clear() {
      for (const k of Object.keys(timers)) clearTimeout(timers[k]);
    },
  };
}

export interface SignalDeps {
  /** 지금 화면이 들고 있는 문서 — 판단은 이걸로 한다(React 의 updater 콜백은 나중에 실행되므로 그 안에서 뽑은 값을 밖에서 읽으면 안 된다) */
  current: () => PlaySessionType;
  /** 문서 갱신 — `(prev) => next` */
  merge: (patch: (prev: PlaySessionType) => PlaySessionType) => void;
  refetchFull: () => Promise<void>;
  refetchState: () => Promise<void>;
  refetchStocks: () => Promise<void>;
  refetchTeam: () => Promise<void>;
  refetchUser: () => Promise<void>;
  onBan: () => void;
  debounceMs?: number;
}

/**
 * 토픽 키별 처리기 — `provider.tsx` 의 `onSignal` 본체.
 *   state  : 값 병합 + (턴·스텝·사례가 바뀌면) state·stocks 조각 재조회
 *   stocks : 조각 재조회(페이로드엔 현재가만 있고 문서는 `price_info[]` 이력이 필요)
 *   team   : 조각 재조회
 *   user   : `{ type: 'ban' }` 이면 강퇴 처리, 아니면 조각 재조회
 *   그 밖  : 문서 전체 재조회(refetch 트리거)
 */
export function createSignalHandler(deps: SignalDeps) {
  const debouncer = createDebouncer(deps.debounceMs ?? 200);
  const schedule = (key: string, fn: () => Promise<void>) => debouncer.schedule(key, () => void fn());
  return {
    handle(key: string, payload: unknown) {
      const p = (payload ?? {}) as Record<string, unknown>;
      switch (key) {
        case 'state': {
          const s = p as unknown as StateSignal;
          if (typeof s.step !== 'string') {
            schedule('full', deps.refetchFull);
            return;
          }
          // ⚠️ 판단은 지금 문서(deps.current)로 동기적으로 한다. React 18 은 setState 의 updater 를 배치해 나중에 실행하므로
          //    updater 안에서 세운 플래그를 바로 읽으면 항상 초기값이다(2026-09-17 실과정 완주에서 발견 — 스텝 전환이
          //    신호가 아니라 만료 5초 유예 재조회로만 진행되고 있었다).
          const { turnOrStepChanged: changed, turnChanged } = mergeState(deps.current(), s);
          if (!changed) {
            // 값만 바뀐 경우(일시정지·만료 시각)만 즉시 끼운다. 턴·스텝이 바뀌면 값을 먼저 끼우지 않는다 — 새 스텝 화면이
            // 옛 stocks·turn_info 로 그려지며 깨진다(1R 1턴 주가현황이 빈 price_info 로 StockCharts 크래시).
            deps.merge((prev) => mergeState(prev, s).next);
          }
          if (turnChanged) {
            // 턴(라운드) 경계에서는 참가자 현금·보유도 서버에서 바뀐다 — 대출 상환(2R 시작)·배당금 수령·전량매수 효과.
            // 조각으로는 놓치므로 문서 전체를 다시 읽는다(게임당 9번뿐이다). 2026-09-17 실과정 완주에서
            // 2R 1턴 「최대」 매수가 상환 전 현금으로 계산돼 '잔액이 부족합니다' 가 나던 결함.
            schedule('full', deps.refetchFull);
            return;
          }
          if (changed || s.scenarioId !== null) {
            // stocks → state 순서: 마지막 병합(state)이 새 스텝을 드러낼 때 stocks 는 이미 새 값이다
            schedule('state', async () => {
              await deps.refetchStocks();
              await deps.refetchState();
            });
          }
          return;
        }
        case 'stocks':
          schedule('stocks', deps.refetchStocks);
          return;
        case 'team':
          schedule('team', deps.refetchTeam);
          return;
        case 'user':
          if (p.type === 'ban') {
            deps.onBan();
            return;
          }
          schedule('user', deps.refetchUser);
          return;
        default:
          schedule('full', deps.refetchFull);
      }
    },
    dispose() {
      debouncer.clear();
    },
  };
}
